import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../_App/Redux/hooks";
import PaginationLayout from "../../_Layouts/Pagination/PaginationLayout";
import { useTranslation } from "react-i18next";
import {
  findArticleMovementSummaryApi,
  exportArticlesMovementSummaryApi,
  Exporting,
  findSalesByFamilleApi,
  SalesByFamille,
  SalesByFamilleLoading,
} from "../../_App/Redux/Slices/mouvement/mouvementSlice";
import { debounce } from "lodash";
import {
  SelectedOrganisations,
  societeId,
} from "../../_App/Redux/Slices/Auth/authSlice";
import Select from "react-select";
import {
  findSousCategorieBySociete,
  ListSousCategories,
} from "../../_App/Redux/Slices/souscategories/souscategorieSlice";
import {
  findProductsMarquesApiBySociete,
  ListProductMarques,
} from "../../_App/Redux/Slices/product_marque/product_marque";

export default function ArticleMovementSummary() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const exporting = useAppSelector(Exporting);

  type MovementSummary = {
    reception: {
      quantity: number;
      amount: number;
    };
    sales: {
      quantity: number;
      amount: number;
    };
    refunds: {
      quantity: number;
      amount: number;
    };
    netSales: {
      quantity: number;
      amount: number;
    };
    stock: number;
    profit: number;
  };

  type ArticleWithMovements = {
    article: {
      _id: string;
      titre: { fr: string };
      code_article: string;
      code_a_barre: string[];
      souscategorie?: { libelle?: { fr: string } };
      articleparent?: { titre: { fr: string } };
      prix_vente_ttc?: number;
    };
    summary: MovementSummary;
  };

  const [articles, setArticles] = useState<any[]>([]);
  const [keyword, setKeyword] = useState("");
  const [refFrom, setRefFrom] = useState("");
  const [refTo, setRefTo] = useState("");

  const [searchType, setSearchType] = useState<
    "article" | "famille" | "reference"
  >("article");
  const today = new Date();

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [date_debut, setDateDebut] = useState(formatDate(today));
  const [date_fin, setDateFin] = useState(formatDate(today));
  const dateDebutWithTime = `${date_debut} 00:00:00`;
  const dateFinWithTime = `${date_fin} 23:59:59`;
  const [spinner, setSpinner] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  // Unique Articles Count

  // Reception States
  const [receptionQty, setReceptionQty] = useState(0);
  const [receptionCost, setReceptionCost] = useState(0);

  // Vente (Gross Sales) States
  const [venteQty, setVenteQty] = useState(0);
  const [venteCost, setVenteCost] = useState(0);

  // Refund (Avoir) States
  const [refundQty, setRefundQty] = useState(0);
  const [refundCost, setRefundCost] = useState(0);

  // Net Vente (Calculated) States
  const [netVenteQty, setNetVenteQty] = useState(0);
  const [netVenteCost, setNetVenteCost] = useState(0);

  const limit = 100; // Fixed 5 items per page as requested

  // State for showing/hiding refund details
  const [showRefundDetails, setShowRefundDetails] = useState(false);
  const societe = useAppSelector(societeId);
  const salesByFamille = useAppSelector(SalesByFamille);
  const salesByFamilleLoading = useAppSelector(SalesByFamilleLoading);

  // Fetch donuts whenever societe or organisation changes

  // Fetch movement summary from new API
  const fetchMovementSummary = async (page: number) => {
    setSpinner(true);
    dispatch(
      findSalesByFamilleApi({
        societe_id: societe,
        organisation_id: selectedorganisation,
      }),
    );
    console.log("selectedSousCategorie", selectedSousCategorie);
    try {
      const params = {
        page,
        limit,
        startDate: dateDebutWithTime,
        endDate: dateFinWithTime,
        search: keyword || undefined,
        searchElement: searchType,
        societe_id: societe,
        organisation_id: selectedorganisation,
        refFrom,
        refTo,
        selectedSousCategorie: selectedSousCategorie,
        selectedMarque: selectedMarque, // 👈 add this
        sortBy: "salesAmount",
      };

      console.log(params);
      const response = await dispatch(findArticleMovementSummaryApi(params));
      if (response.payload?.success) {
        setArticles(response.payload.data || []);
        setTotalPages(response.payload.pagination?.totalPages || 1);
        setCurrentPage(response.payload.pagination?.page || 1);
        const stats = response.payload.pagination?.totals;

        if (stats) {
          setTotalItems(stats.total || 0);

          setReceptionQty(stats.receptionQty || 0);
          setReceptionCost(stats.receptionCost || 0);

          setVenteQty(stats.venteQty || 0);
          setVenteCost(stats.venteCost || 0);

          setRefundQty(stats.refundQty || 0);
          setRefundCost(stats.refundCost || 0);

          // Calculate the Net Vente (Gross Sales - Refunds)
          setNetVenteQty((stats.venteQty || 0) + (stats.refundQty || 0));
          setNetVenteCost((stats.venteCost || 0) + (stats.refundCost || 0));
        } else {
          // Reset to 0 if no data is returned
          setTotalItems(0);
          setReceptionQty(0);
          setReceptionCost(0);
          setVenteQty(0);
          setVenteCost(0);
          setRefundQty(0);
          setRefundCost(0);
          setNetVenteQty(0);
          setNetVenteCost(0);
        }
      }
    } catch (error) {
      console.error("Error fetching movement summary:", error);
    } finally {
      setSpinner(false);
    }
  };

  // Debounced search
  // const debouncedSearch = debounce(() => {
  //   if (date_debut && date_fin) {
  //     fetchMovementSummary(1);
  //   }
  // }, 500);

  // Handle date changes
  useEffect(() => {
    fetchMovementSummary(1);
  }, [searchType]);

  // Handle page change
  useEffect(() => {
    fetchMovementSummary(currentPage);
  }, [currentPage]);
  useEffect(() => {
    if (societe) {
      const fetchData = async () => {
        await dispatch(findSousCategorieBySociete(societe));
        await dispatch(findProductsMarquesApiBySociete(societe));
      };
      fetchData();
    }
  }, [societe]);
  const sousCategories = useAppSelector(ListSousCategories);
  const [souscategoriesOptions, setsouscategoriesdOptions] = useState<any[]>(
    [],
  );
  const ListProductMarque = useAppSelector(ListProductMarques);

  const optionmarque = ListProductMarque.map((item: any) => ({
    value: item._id,
    label: item.libelle.fr,
  }));
  const [selectedCategorie, setSelectedCategorie] = useState<any[]>([]);
  const [selectedSousCategorie, setSelectedSousCategorie] = useState<any[]>([]);
  const [selectedMarque, setSelectedMarque] = useState<any[]>([]);

  const handleSelectChangeSousCategories = (selectedOption: any) => {
    console.log(selectedOption);
    const souscategorie = selectedOption.map((item: any) => item.value);
    setSelectedSousCategorie(souscategorie);
  };
  const handleSelectMarque = (selectedOption: any) => {
    const marque = selectedOption.map((item: any) => item.value);
    setSelectedMarque(marque);
  };
  useEffect(() => {
    setsouscategoriesdOptions(
      sousCategories?.map((option) => ({
        value: option._id,
        label: option.libelle.fr,
      })),
    );
  }, [sousCategories]);
  // Calculate totals
  // Calculate totals
  const totals = articles.reduce(
    (acc, { summary }) => {
      return {
        receptionQty: acc.receptionQty + summary.reception.quantity,
        receptionAmount: acc.receptionAmount + summary.reception.amount,
        salesQty: acc.salesQty + summary.sales.quantity,
        salesAmount: acc.salesAmount + summary.sales.amount,
        refundQty: acc.refundQty + summary.refunds.quantity,
        refundAmount: acc.refundAmount + summary.refunds.amount,
        netSalesQty: acc.netSalesQty + summary.netSales.quantity,
        netSalesAmount: acc.netSalesAmount + summary.netSales.amount,
        profit: acc.profit + summary.profit,
        stock: acc.stock + summary.stock,
        // ✅ ADD THIS
        inventory: {
          entree:
            acc.inventory.entree + (summary.inventory?.entree?.quantity ?? 0),
          sortie:
            acc.inventory.sortie + (summary.inventory?.sortie?.quantity ?? 0),
        },
      };
    },
    {
      receptionQty: 0,
      receptionAmount: 0,
      salesQty: 0,
      salesAmount: 0,
      refundQty: 0,
      refundAmount: 0,
      netSalesQty: 0,
      netSalesAmount: 0,
      profit: 0,
      stock: 0,
      // ✅ ADD THIS
      inventory: { entree: 0, sortie: 0 },
    },
  );

  // Function to handle CSV export (alternative)
  const handleExportCSV = async () => {
    try {
      // Fetch all data without pagination
      const allDataParams = {
        startDate: date_debut,
        endDate: date_fin,
        search: keyword || undefined,
        societe_id: societe,
        organisation_id: selectedorganisation,
        refFrom,
        refTo,
        page: 1,
        limit: 500000, // Large limit to get all data
        sortBy: "salesAmount",
      };

      const response = await dispatch(
        findArticleMovementSummaryApi(allDataParams),
      );

      if (response.payload?.success && response.payload.data) {
        // Convert to CSV
        const csvContent = convertToCSV(response.payload.data);

        // Download CSV file
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `export-mouvements-${new Date().toISOString().split("T")[0]}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("CSV export error:", error);
    }
  };

  // Helper function to convert data to CSV
  const convertToCSV = (data: any[]) => {
    const headers = [
      "Famille",
      "Ref",
      "Article",
      "Code Barre",
      "Achat_Qty",
      "Achat_Montant",
      "Vente_Qty",
      "Vente_Montant",
      "Retour_Qty",
      "Retour_Montant",
      "Net_Qty",
      "Net_Montant",
      "Profit",
    ];

    const rows = data.map(({ article, summary }) => [
      article.souscategorie?.libelle?.fr || "-",
      article.articleparent?.titre?.fr || article.code_article,
      article.titre?.fr || "-",
      article.code_a_barre?.[0] || "-",
      summary.reception.quantity,
      summary.reception.amount.toFixed(3),
      summary.sales.quantity,
      summary.sales.amount.toFixed(3),
      summary.refunds.quantity,
      summary.refunds.amount.toFixed(3),
      summary.netSales.quantity,
      summary.netSales.amount.toFixed(3),
      summary.profit.toFixed(3),
    ]);

    return [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
  };
  const OrganisationList = useAppSelector(SelectedOrganisations);

  const [OrganisationOption, setOrganisationOption] = useState<any[]>([]);
  const [selectedorganisation, setselectedorganisation] = useState<any[]>([]);
  useEffect(() => {
    if (OrganisationList)
      setOrganisationOption(
        OrganisationList?.map((option) => ({
          value: option._id,
          label: option.nom,
        })),
      );
  }, [OrganisationList]);
  const handelSelectOrganisation = (selectedOption: any) => {
    const listOrganisations = selectedOption.map((item: any) => item.value);
    setselectedorganisation(listOrganisations);
  };

  return (
    <div className="container-fluid">
      {/* Header Section */}
      <div className="row bg-white p-4 mb-4 rounded shadow-sm">
        <div className="col-12 mb-3">
          <h3 className="mb-0">{t("Mouvements d'articles")}</h3>
          <p className="text-muted mb-0">{t("Achats vs Ventes")}</p>
        </div>
        <div className="row">
          {" "}
          <div className="col-md-2 mb-3">
            <label className="form-label">{t("Date début")}</label>
            <input
              type="date"
              className="form-control"
              value={date_debut}
              onChange={(e) => setDateDebut(e.target.value)}
            />
          </div>
          <div className="col-md-2 mb-3">
            <label className="form-label">{t("Date fin")}</label>
            <input
              type="date"
              className="form-control"
              value={date_fin}
              onChange={(e) => setDateFin(e.target.value)}
            />
          </div>
          <div className="col-2">
            <label>{t("Organisation")}</label>
            <Select
              isMulti
              options={OrganisationOption}
              isSearchable={true}
              onChange={handelSelectOrganisation}
              value={selectedorganisation?.map((item: any) =>
                OrganisationOption?.find((cat: any) => cat.value === item),
              )}
            />
          </div>
          <div className="col-2">
            <div className="form-group">
              <label htmlFor="sous">
                {" "}
                <span style={{ color: "red" }}></span>
                {t("sous categorie")}
              </label>
              <Select
                isMulti
                options={souscategoriesOptions}
                isSearchable={true}
                onChange={handleSelectChangeSousCategories}
                value={selectedSousCategorie?.map((item: any) =>
                  souscategoriesOptions?.find((cat: any) => cat.value === item),
                )}
              />
            </div>
          </div>
          <div className="col-2">
            <div className="form-group">
              <label htmlFor="sous">
                {" "}
                <span style={{ color: "red" }}></span>
                {t("Marque")}
              </label>
              <Select
                isMulti
                options={optionmarque}
                isSearchable={true}
                onChange={handleSelectMarque}
                value={selectedMarque?.map((item: any) =>
                  optionmarque?.find((cat: any) => cat.value === item),
                )}
              />
            </div>
          </div>
          <div className="col-2">
            <label className="form-label">{t("Réf de")}</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder={t("Réference 1")}
                value={refFrom}
                onChange={(e) => setRefFrom(e.target.value)}
              />
              {refFrom && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setRefFrom("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>{" "}
          <div className="col-2">
            <label className="form-label">{t("Réf à")}</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder={t("Réference 2")}
                value={refTo}
                onChange={(e) => setRefTo(e.target.value)}
              />
              {refTo && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setRefTo("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div className="col-md-9 mb-2">
            <label className="form-label">{t("Recherche")}</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder={t("Code ou nom d'article")}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              {keyword && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setKeyword("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div
            className="col-md-1"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "2px",
            }}
          >
            <button
              style={{
                marginTop: "16px",
              }}
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => fetchMovementSummary(1)}
            >
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>
        <div className="row"></div>
        {/* Date Filters */}

        {/* Options */}
        <div className="col-md-6 mb-3">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="showRefundDetails"
              checked={showRefundDetails}
              onChange={(e) => setShowRefundDetails(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="showRefundDetails">
              {t("Afficher les détails des retours")}
            </label>
          </div>
        </div>

        <div className="col-md-6 mb-3 text-end d-flex justify-content-end align-items-center gap-2">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn ${
                searchType === "article" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setSearchType("article")}
            >
              {t("Par article")}
            </button>
            <button
              type="button"
              className={`btn ${
                searchType === "famille" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setSearchType("famille")}
            >
              {t("Par famille")}
            </button>{" "}
            <button
              type="button"
              className={`btn ${
                searchType === "reference"
                  ? "btn-primary"
                  : "btn-outline-primary"
              }`}
              onClick={() => setSearchType("reference")}
            >
              {t("Par référence")}
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      {articles.length > 0 && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-info d-flex justify-content-between align-items-center">
              <div>
                <strong>{totalItems}</strong> {t("articles trouvés")} •
                <span className="ms-2">
                  {t("Page")} {currentPage} {t("sur")} {totalPages}
                </span>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-success d-flex align-items-center"
                  onClick={handleExportCSV}
                  disabled={exporting || articles.length === 0}
                >
                  {exporting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      {t("Export en cours...")}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-file-earmark-excel me-2"></i>
                      {t("Exporter Excel")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      {searchType !== "famille" ? (
                        <>
                          <th rowSpan={2} className="text-center align-middle">
                            {t("Référence")}
                          </th>
                          {searchType === "article" && (
                            <th
                              rowSpan={2}
                              className="text-center align-middle"
                            >
                              {t("Article")}
                            </th>
                          )}
                          <th rowSpan={2} className="text-center align-middle">
                            {t("Code Barre")}
                          </th>
                        </>
                      ) : null}
                      <th rowSpan={2} className="text-center align-middle">
                        {t("Catégorie")}
                      </th>

                      <th
                        colSpan={2}
                        className="text-center bg-info bg-opacity-10"
                      >
                        {t("ACHATS")}
                      </th>

                      <th
                        colSpan={2}
                        className="text-center bg-warning bg-opacity-10"
                      >
                        {t("VENTES")}
                      </th>

                      {showRefundDetails && (
                        <th
                          rowSpan={2}
                          className="text-center align-middle bg-danger bg-opacity-10"
                        >
                          {t("RETOURS")}
                        </th>
                      )}

                      <th
                        colSpan={2}
                        className="text-center bg-success bg-opacity-10"
                      >
                        {t("NET")}
                      </th>

                      <th
                        rowSpan={2}
                        className="text-center align-middle bg-secondary bg-opacity-10"
                      >
                        {t("Reste")}
                      </th>

                      <th
                        colSpan={2}
                        className="text-center bg-primary bg-opacity-10"
                      >
                        {t("INVENTAIRE")}
                      </th>

                      <th rowSpan={2} className="text-center align-middle">
                        {t("Stock")}
                      </th>

                      <th rowSpan={2} className="text-center align-middle">
                        {t("Profit")}
                      </th>
                    </tr>

                    {/* Sub-header */}
                    <tr>
                      {/* Achat */}
                      <th className="text-center">{t("Qty")}</th>
                      <th className="text-center">{t("Montant")}</th>
                      {/* Vente */}
                      <th className="text-center">{t("Qty")}</th>
                      <th className="text-center">{t("Montant")}</th>
                      {/* Net */}
                      <th className="text-center">{t("Qty")}</th>
                      <th className="text-center">{t("Montant")}</th>
                      {/* Inventaire */}
                      <th className="text-center">{t("Sortie")}</th>{" "}
                      <th className="text-center">{t("Entrée")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {articles.length > 0 ? (
                      articles.map(({ article, summary }, index) => (
                        <tr
                          key={article._id}
                          style={{ cursor: "pointer" }}
                          className="hover-row"
                        >
                          {searchType !== "famille" ? (
                            <>
                              <td className="text-center">
                                {article.code_article?.split("-")[0]}
                              </td>
                              {searchType === "article" && (
                                <td className="text-center">
                                  {article.titre?.fr || "-"}
                                </td>
                              )}
                              <td className="text-center">
                                {article.code_a_barre?.[0] || "-"}
                              </td>
                            </>
                          ) : null}
                          <td className="text-center">
                            {searchType === "famille"
                              ? article.libelle?.fr
                              : article.souscategorie?.libelle?.fr || "-"}
                          </td>
                          {/* Achat */}
                          <td className="text-center">
                            <span className="badge bg-info">
                              {summary.reception.quantity}
                            </span>
                          </td>
                          <td className="text-center text-info fw-bold">
                            {summary.reception.amount.toFixed(3)} TND
                          </td>
                          {/* Vente */}
                          <td className="text-center">
                            <span className="badge bg-warning">
                              {summary.sales.quantity}
                            </span>
                          </td>
                          <td className="text-center text-warning fw-bold">
                            {summary.sales.amount.toFixed(3)} TND
                          </td>
                          {/* Retours (conditionnel) */}
                          {showRefundDetails && (
                            <td className="text-center">
                              <span className="badge bg-danger">
                                {summary.refunds.quantity}
                              </span>
                              <div className="small text-danger">
                                {summary.refunds.amount.toFixed(3)} TND
                              </div>
                            </td>
                          )}
                          {/* Net */}
                          <td className="text-center">
                            <span
                              className={`badge ${
                                summary.netSales.quantity >= 0
                                  ? "bg-success"
                                  : "bg-danger"
                              }`}
                            >
                              {summary.netSales.quantity}
                            </span>
                          </td>
                          <td
                            className={`text-center fw-bold ${
                              summary.netSales.amount >= 0
                                ? "text-success"
                                : "text-danger"
                            }`}
                          >
                            {summary.netSales.amount.toFixed(3)} TND
                          </td>
                          {/* Reste */}
                          <td className="text-center">
                            <span
                              className={`badge ${
                                summary.reception.quantity -
                                  summary.netSales.quantity >=
                                0
                                  ? "bg-secondary"
                                  : "bg-danger"
                              }`}
                            >
                              {summary.reception.quantity -
                                summary.netSales.quantity}
                            </span>
                          </td>
                          {/* Inventaire */}
                          <td className="text-center">
                            <span className="badge bg-primary">
                              {summary.inventory.sortie.quantity}
                            </span>
                          </td>{" "}
                          <td className="text-center">
                            <span className="badge bg-primary">
                              {summary.inventory.entree.quantity}
                            </span>
                          </td>
                          {/* Stock */}
                          <td className="text-center fw-bold">
                            {summary.reception.quantity -
                              summary.netSales.quantity -
                              Math.abs(summary.inventory.sortie.quantity) +
                              summary.inventory.entree.quantity}
                          </td>
                          {/* Profit */}
                          <td
                            className={`text-center fw-bold ${
                              summary.profit >= 0
                                ? "text-success"
                                : "text-danger"
                            }`}
                          >
                            {summary.profit.toFixed(3)} TND
                          </td>
                        </tr>
                      ))
                    ) : !spinner ? (
                      <tr>
                        <td
                          colSpan={
                            (searchType === "famille"
                              ? 1
                              : searchType === "article"
                              ? 4
                              : 3) + (showRefundDetails ? 9 : 8)
                          }
                          className="text-center py-5"
                        >
                          <div className="text-muted">
                            <i
                              className="bi bi-inbox"
                              style={{ fontSize: "3rem" }}
                            ></i>
                            <p className="mt-3">
                              {t(
                                "Aucun mouvement trouvé pour la période sélectionnée.",
                              )}
                            </p>
                            {date_debut && date_fin && (
                              <small>
                                {t("Période")}: {date_debut} {t("au")}{" "}
                                {date_fin}
                              </small>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </tbody>

                  {/* Totals Footer */}
                  {articles.length > 0 && (
                    <tfoot className="table">
                      <tr>
                        <th
                          colSpan={
                            searchType === "famille"
                              ? 1
                              : searchType === "article"
                              ? 4
                              : 3
                          }
                          className="text-end"
                        >
                          {t("TOTAUX")}
                        </th>
                        {/* Achat */}
                        <th className="text-center">
                          <div className="fw-bold">{receptionQty}</div>
                        </th>
                        <th className="text-center">
                          <div className="fw-bold text-info">
                            {receptionCost.toFixed(3)} TND
                          </div>
                        </th>
                        {/* Vente */}
                        <th className="text-center">
                          <div className="fw-bold">{venteQty}</div>
                        </th>
                        <th className="text-center">
                          <div className="fw-bold text-warning">
                            {venteCost.toFixed(3)} TND
                          </div>
                        </th>
                        {/* Retours (conditionnel) */}
                        {showRefundDetails && (
                          <th className="text-center">
                            <div className="fw-bold">{Math.abs(refundQty)}</div>
                            <div className="small text-danger">
                              {refundCost.toFixed(3)} TND
                            </div>
                          </th>
                        )}
                        {/* Net */}
                        <th className="text-center">
                          <div className="fw-bold">{netVenteQty}</div>
                        </th>
                        <th
                          className={`text-center fw-bold ${
                            netVenteCost >= 0 ? "text-success" : "text-danger"
                          }`}
                        >
                          {netVenteCost.toFixed(3)} TND
                        </th>
                        {/* Reste */}
                        <th className="text-center">
                          <div className="fw-bold">
                            {receptionQty - netVenteQty}
                          </div>
                        </th>
                        {/* Inventaire */}
                        <th className="text-center">
                          <div className="fw-bold text-primary">
                            {totals.inventory?.sortie ?? 0}
                          </div>
                        </th>{" "}
                        <th className="text-center">
                          <div className="fw-bold text-primary">
                            {totals.inventory?.entree ?? 0}
                          </div>
                        </th>
                        {/* Stock */}
                        <th className="text-center">
                          <div className="fw-bold">
                            {receptionQty -
                              netVenteQty -
                              Math.abs(totals.inventory?.sortie ?? 0) +
                              (totals.inventory?.entree ?? 0)}
                          </div>
                        </th>
                        {/* Profit */}
                        <th
                          className={`text-center fw-bold ${
                            totals.profit >= 0 ? "text-success" : "text-danger"
                          }`}
                        >
                          {totals.profit.toFixed(3)} TND
                        </th>
                      </tr>
                    </tfoot>
                  )}
                </table>

                {spinner && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">
                        {t("Chargement...")}
                      </span>
                    </div>
                    <p className="mt-2 text-muted">
                      {t("Récupération des données...")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {articles.length > 0 && totalPages > 1 && (
        <div className="row mt-4">
          <div className="col-12">
            <PaginationLayout
              index={currentPage}
              nbrpage={currentPage}
              totalpages={totalPages}
              setindex={(newPage: number) => setCurrentPage(newPage)}
            />
          </div>
        </div>
      )}
      {/* Ventes par famille — donuts */}
      {salesByFamille && (
        <div className="row mt-4 bg-white p-4 rounded shadow-sm">
          <div className="col-12 mb-3">
            <h5 className="mb-0">{t("Ventes par famille")}</h5>
            {/* <p className="text-muted mb-0">
              {t("Aujourd'hui · Ce mois · Cette année")}
            </p> */}
          </div>

          {salesByFamilleLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : (
            <>
              <DonutPanel
                title={t("Aujourd'hui")}
                canvasId="donut-today"
                items={salesByFamille.today}
              />
              <DonutPanel
                title={t(
                  "Mois " +
                    new Date().toLocaleString("fr-FR", { month: "long" }),
                )}
                canvasId="donut-month"
                items={salesByFamille.month}
              />
              <DonutPanel
                title={t("Année " + new Date().getFullYear())}
                canvasId="donut-year"
                items={salesByFamille.year}
              />
            </>
          )}
        </div>
      )}

      {/* CSS Styles */}
    </div>
  );
}
const PALETTE = [
  "#7F77DD",
  "#E24B4A",
  "#1D9E75",
  "#EF9F27",
  "#D85A30",
  "#378ADD",
  "#D4537E",
  "#639922",
  "#BA7517",
  "#993556",
];

function fmt(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString("fr-FR", { maximumFractionDigits: 3 });
}

function DonutPanel({
  title,
  canvasId,
  items,
}: {
  title: string;
  canvasId: string;
  items: { familleName: string; totalAmount: number }[];
}) {
  const chartRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (!items?.length) return;

    // Dynamically import Chart.js only once
    import("chart.js/auto").then(({ default: Chart }) => {
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
      if (!canvas) return;

      // Destroy previous instance if any
      if (chartRef.current) {
        chartRef.current.destroy();
      }

      const total = items.reduce((s, i) => s + i.totalAmount, 0);

      chartRef.current = new Chart(canvas, {
        type: "doughnut",
        data: {
          labels: items.map((i) => i.familleName),
          datasets: [
            {
              data: items.map((i) => i.totalAmount),
              backgroundColor: items.map(
                (_, idx) => PALETTE[idx % PALETTE.length],
              ),
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "62%",
          plugins: { legend: { display: false } },
        },
      });
    });

    return () => {
      chartRef.current?.destroy();
    };
  }, [items, canvasId]);

  if (!items?.length) {
    return (
      <div className="col-md-4 text-center text-muted py-4">
        {title} — Aucune vente
      </div>
    );
  }

  const total = items.reduce((s, i) => s + i.totalAmount, 0);

  return (
    <div className="col-md-4">
      <p className="text-center fw-500 mb-2" style={{ fontWeight: 500 }}>
        {title}
      </p>
      <div style={{ position: "relative", height: 200 }}>
        <canvas id={canvasId} />
      </div>
      <div className="mt-3" style={{ maxHeight: 180, overflowY: "auto" }}>
        {items.map((item, i) => (
          <div
            key={item.familleName}
            className="d-flex justify-content-between align-items-center mb-1"
            style={{ fontSize: 12 }}
          >
            <div className="d-flex align-items-center gap-2">
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 2,
                  background: PALETTE[i % PALETTE.length],
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              <span
                className="text-muted text-truncate"
                style={{ maxWidth: 120 }}
              >
                {item.familleName}
              </span>
            </div>
            <span className="fw-bold">
              {fmt(item.totalAmount)}{" "}
              <span className="text-muted fw-normal">
                ({((item.totalAmount / total) * 100).toFixed(1)}%)
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
